<template>
  <slot
    :data="data"
    :error="error"
    :loading="loading"
    :fetch-data="fetchData"
  />
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import type {
  IAuto5Response,
  IItem,
  IPlacementSettings,
  NormalizedData,
  ICallToAction,
  ISearchResults,
} from '@/types'
import { useSettingsStore } from '@/stores/settings'
import { doSearch } from '@/api'
import { config } from '@/config/generated-config'

defineSlots<{
  default(props: {
    data: NormalizedData | null
    error: Error | null
    loading: boolean
    fetchData: () => Promise<void>
  }): any
}>()

const data = ref<NormalizedData | null>(null)
const error = ref<Error | null>(null)
const loading = ref(false)

const controller = new AbortController()

const settings = useSettingsStore()
const placementSettings: IPlacementSettings = settings.placementSettings
const activeFilters = placementSettings?.activeFilters ?? {}
const callToAction: ICallToAction = placementSettings?.callToAction ?? {}
const ctaTypography = callToAction?.typography ?? {}
const ctaBorder = callToAction?.border ?? {}
const ctaIcon = callToAction?.icon ?? {}
const ctaLink = callToAction?.link ?? '/vehicle/{{vin}}'
const numberOfVehicles = placementSettings?.numberOfVehicles ?? 4
const activeVehicles = placementSettings?.activeVehicles ?? []

const fetchData = async () => {
  loading.value = true

  const sortBy = placementSettings?.sortBy ?? 'display_price'
  const sortDirection = placementSettings?.sortDirection ?? 'asc'
  const secondarySorts = placementSettings?.secondarySorts ?? []

  // build VIN filters
  let activeVehicleVINs = []

  if (activeVehicles.length > 0) {
    activeVehicleVINs = activeVehicles.map((vehicle: any) => vehicle.vin)
  }

  const allFilters = {
    ...activeFilters,
    vin: activeVehicleVINs,
    excluded: { ...placementSettings.excludedFilters },
  }

  const endpoint = config.VITE_INVENTORY_API
  const result: ISearchResults = await doSearch(endpoint, {
    // Required by kombucha inventory API for usage cost attribution
    juid: settings.accountId,
    product: 'micro_srp',
    accountIds: settings.inventoryIds,
    baseFilters: {},
    filters: allFilters,
    searchKeyword: '',
    size: numberOfVehicles,
    start: 0,
    sortField: sortBy,
    sortDirection: sortDirection,
    secondarySorts,
    returnFields: '',
  })

  const itemsToShow = result?.items ?? []

  if (itemsToShow.length === 0) {
    // Remove warning for expected scenario - empty results are normal
    // console.warn('Micro SRP: No items found in response')
  }

  const items: IItem[] = itemsToShow.map(item => {
    try {
      return {
        id: item.id,
        fields: item,
        image: item.visual?.image?.source ?? '',
        link: ctaLink.replace(/{{(.*?)}}/g, (_, key) => item?.[key] ?? ''),
      }
    } catch (e) {
      // Keep this error log - it's actionable for debugging
      console.error(`Failed to parse image data for item ${item.id}`, e)
      return {
        id: item.id,
        fields: item,
        image: '',
        link: ctaLink,
      }
    }
  })

  data.value = {
    count: result?.count ?? 0,
    facets: {},
    items: items,
  }

  loading.value = false

  // try {
  //   const queries = []
  //   const inventoryIds: number[] = settings.inventoryIds
  //
  //   if (inventoryIds.length > 0) {
  //     const accountIdQuery = buildAccountsQuery(inventoryIds)
  //     queries.push(`(${accountIdQuery})`)
  //   } else {
  //     return
  //   }
  //
  //   let activeVehicleVINs = []
  //
  //   if (activeVehicles.length > 0) {
  //     activeVehicleVINs = activeVehicles.map((vehicle: any) => vehicle.vin)
  //   }
  //
  //   const combinedQueries = queries.join(' AND ')
  //   const sortBy = placementSettings?.sortBy ?? 'display_price'
  //   const sortDirection = placementSettings?.sortDirection ?? 'asc'
  //   const sort = `has_price desc,has_image desc,${sortBy} ${sortDirection}`
  //
  //   const query = `q=(${combinedQueries})&q.parser=lucene&size=${numberOfVehicles}&sort=${sort}`
  //
  //   const response = await fetch(`${API_URL}?${query}`, {
  //     signal: controller.signal,
  //   })
  //
  //   if (!response.ok) {
  //     error.value = new Error(`HTTP error! status: ${response.status}`)
  //   }
  //
  //   const responseData = await response.json()
  //   data.value = normalizeData(responseData, ctaLink)
  // } catch (err) {
  //   error.value =
  //     err instanceof Error ? err : new Error('Unknown error occurred')
  // } finally {
  //   loading.value = false
  // }
}

// Optional auto-refresh functionality
const refreshInterval = ref<number | null>(null)

// const startAutoRefresh = (intervalMs: number = 30000) => {
//   refreshInterval.value = window.setInterval(fetchData, intervalMs)
// }

const stopAutoRefresh = () => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value)
    refreshInterval.value = null
  }
}

onMounted(() => {
  fetchData()
})

onUnmounted(() => {
  controller.abort()
  stopAutoRefresh()
})

const normalizeData = (
  responseData: IAuto5Response,
  ctaLink: string,
): NormalizedData => {
  const hits = responseData?.hits?.hit ?? []

  if (hits.length === 0) {
    console.log('Micro SRP: No data found in response')
  }

  const items: IItem[] = hits.map(item => {
    try {
      const imageData = JSON.parse(item.fields.image)
      return {
        id: item.id,
        fields: item.fields,
        image: imageData.Source,
        link: ctaLink.replace(
          /{{(.*?)}}/g,
          (_, key) => item?.fields?.[key] ?? '',
        ),
      }
    } catch (e) {
      console.error(`Failed to parse image data for item ${item.id}`, e)
      return {
        id: item.id,
        fields: item.fields,
        image: '',
        link: ctaLink,
      }
    }
  })

  return {
    count: responseData?.hits?.found ?? 0,
    facets: responseData?.facets ?? {},
    items,
  }
}
</script>
